import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, Path, Plus, ArrowRight, CheckCircle } from "@phosphor-icons/react";

/**
 * ChangeRouteModal
 * ─────────────────
 * Supervisor-only quick-access modal exposed from the Vessel Activity dashboard.
 * Lets the on-shift supervisor stop the current route and pick a different one
 * when loading has to move to another area of the terminal.
 *
 * Reuses the same backend contract as the Conveyor Systems admin panel:
 *   GET  /conveyor-routes?division=RBTG
 *   GET  /vessels/{id}/conveyor-routes          (segments incl. inactive)
 *   POST /vessels/{id}/conveyor-routes/activate
 *   POST /vessels/{id}/conveyor-routes/{active_id}/deactivate
 */
export default function ChangeRouteModal({ vessel, onClose, onChanged }) {
    const vesselId = vessel?.id;
    const div = vessel?.division || "RBTG";
    const [availableRoutes, setAvailableRoutes] = useState([]);
    const [segments, setSegments] = useState([]);
    const [pickedRouteId, setPickedRouteId] = useState("");
    const [note, setNote] = useState("");
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);

    const activeRoutes = useMemo(() => segments.filter((s) => s.active), [segments]);
    const pickableRoutes = useMemo(
        () => availableRoutes.filter((r) => !activeRoutes.find((a) => a.route_id === r.id)),
        [availableRoutes, activeRoutes],
    );

    const load = async () => {
        if (!vesselId) return;
        setLoading(true);
        try {
            const [routesRes, segsRes] = await Promise.all([
                api.get("/conveyor-routes", { params: { division: div } }),
                api.get(`/vessels/${vesselId}/conveyor-routes`),
            ]);
            setAvailableRoutes(routesRes.data || []);
            setSegments(segsRes.data || []);
        } catch (err) {
            toast.error("Could not load routes — please try again");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vesselId, div]);

    const stopRoute = async (segId, name) => {
        if (!window.confirm(`Stop the "${name}" route on this vessel?`)) return;
        setBusy(true);
        try {
            await api.post(`/vessels/${vesselId}/conveyor-routes/${segId}/deactivate`);
            toast.success(`Stopped ${name}`);
            await load();
            onChanged && onChanged();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not stop the route");
        } finally {
            setBusy(false);
        }
    };

    const activate = async () => {
        if (!pickedRouteId) return;
        setBusy(true);
        try {
            const picked = pickableRoutes.find((r) => r.id === pickedRouteId);
            await api.post(`/vessels/${vesselId}/conveyor-routes/activate`, {
                route_id: pickedRouteId,
                notes: note.trim() || undefined,
            });
            toast.success(`Loading now on ${picked?.name || "new route"}`);
            setPickedRouteId("");
            setNote("");
            await load();
            onChanged && onChanged();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not activate the new route");
        } finally {
            setBusy(false);
        }
    };

    // Convenience helper — swap in one flow: stop current + start new
    const swapAll = async () => {
        if (!pickedRouteId) return;
        if (!window.confirm(
            `This will stop ALL currently active routes and start the new one. Continue?`
        )) return;
        setBusy(true);
        try {
            // Stop every active route first
            for (const a of activeRoutes) {
                await api.post(`/vessels/${vesselId}/conveyor-routes/${a.id}/deactivate`);
            }
            const picked = pickableRoutes.find((r) => r.id === pickedRouteId);
            await api.post(`/vessels/${vesselId}/conveyor-routes/activate`, {
                route_id: pickedRouteId,
                notes: note.trim() || `Switched from ${activeRoutes.map((a) => a.route_name).join(", ") || "previous route"}`,
            });
            toast.success(`Switched to ${picked?.name || "new route"}`);
            setPickedRouteId("");
            setNote("");
            await load();
            onChanged && onChanged();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not swap routes");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-center justify-center p-3" data-testid="change-route-modal">
            <div className="bg-white rounded-md shadow-lg w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
                <div className="p-3 border-b border-slate-200 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-md bg-[var(--grindrod-navy,#0F1E3D)] text-white flex items-center justify-center">
                        <Path size={16} weight="bold" />
                    </span>
                    <div className="flex-1 min-w-0">
                        <div className="font-display font-black uppercase tracking-wide">Change loading route</div>
                        <div className="text-xs text-slate-500">Stop the current route and start loading from a different area.</div>
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="change-route-close">
                        <X size={20} weight="bold" />
                    </button>
                </div>

                <div className="p-4 space-y-4 overflow-y-auto">
                    {/* Currently active */}
                    <div>
                        <div className="uppercase-label mb-2">Currently active</div>
                        {loading ? (
                            <div className="text-sm text-slate-400 py-2">Loading…</div>
                        ) : activeRoutes.length === 0 ? (
                            <div className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-md p-3 text-center" data-testid="no-active-route-here">
                                No route active — pick one below to start loading.
                            </div>
                        ) : (
                            <ul className="space-y-2" data-testid="active-routes-in-modal">
                                {activeRoutes.map((a) => (
                                    <li key={a.id} className="border border-slate-200 bg-slate-50 rounded-md p-3 flex items-start gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="g-pill" style={{ background: "var(--grindrod-green-bg, #D1FAE5)", color: "#047857", border: "1px solid var(--grindrod-green, #10B981)" }}>LIVE</span>
                                                <span className="font-display font-bold text-sm text-[var(--grindrod-navy,#0F1E3D)]">{a.route_name}</span>
                                            </div>
                                            {a.description && (
                                                <div className="text-xs text-slate-500 mt-0.5">{a.description}</div>
                                            )}
                                            <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-1">
                                                {(a.conveyors || []).map((c) => (
                                                    <span key={c} className="px-1.5 py-0.5 rounded-sm border border-slate-300 bg-white tabular text-slate-700">{c}</span>
                                                ))}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => stopRoute(a.id, a.route_name)}
                                            disabled={busy}
                                            className="btn-outline !py-1 !px-2 text-[11px] inline-flex items-center gap-1 !border-[var(--grindrod-red,#DC2626)] !text-[var(--grindrod-red,#DC2626)] disabled:opacity-40"
                                            data-testid={`change-route-stop-${a.id}`}
                                        >
                                            <X size={11} weight="bold" /> Stop
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Pick new route */}
                    <div className="pt-3 border-t border-slate-100">
                        <div className="uppercase-label mb-2">Switch to a different route</div>
                        <select
                            value={pickedRouteId}
                            onChange={(e) => setPickedRouteId(e.target.value)}
                            className="g-input w-full text-sm mb-2"
                            data-testid="change-route-select"
                            disabled={busy || pickableRoutes.length === 0}
                        >
                            <option value="">{pickableRoutes.length === 0 ? "— No other routes available —" : "— Pick a new route —"}</option>
                            {pickableRoutes.map((r) => (
                                <option key={r.id} value={r.id}>
                                    {r.name}{r.description ? ` — ${r.description}` : ""}
                                </option>
                            ))}
                        </select>
                        {pickedRouteId && (() => {
                            const picked = pickableRoutes.find((r) => r.id === pickedRouteId);
                            return picked ? (
                                <div className="border border-blue-100 bg-blue-50 rounded-md p-2 text-xs mb-2">
                                    <div className="font-semibold text-[var(--grindrod-navy,#0F1E3D)] mb-1">{picked.name} path</div>
                                    <div className="flex flex-wrap items-center gap-1">
                                        {(picked.conveyors || []).map((c, idx) => (
                                            <React.Fragment key={c}>
                                                <span className="px-1.5 py-0.5 rounded-sm border border-slate-300 bg-white tabular text-slate-700">{c}</span>
                                                {idx < (picked.conveyors || []).length - 1 && (
                                                    <ArrowRight size={11} weight="bold" className="text-slate-400" />
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </div>
                            ) : null;
                        })()}
                        <label className="uppercase-label block mb-1">Reason (optional)</label>
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="e.g. Conveyor breakdown on the current route, moving to fallback path"
                            rows={2}
                            className="g-input w-full text-sm"
                            data-testid="change-route-note"
                        />
                    </div>
                </div>

                <div className="p-3 border-t border-slate-200 flex flex-col-reverse sm:flex-row justify-end gap-2">
                    <button onClick={onClose} className="btn-outline text-sm" data-testid="change-route-cancel" disabled={busy}>
                        Cancel
                    </button>
                    {activeRoutes.length > 0 ? (
                        <button
                            onClick={swapAll}
                            disabled={!pickedRouteId || busy}
                            className="btn-primary text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40"
                            data-testid="change-route-swap"
                        >
                            <CheckCircle size={14} weight="bold" />
                            {busy ? "Switching…" : "Stop current & switch"}
                        </button>
                    ) : (
                        <button
                            onClick={activate}
                            disabled={!pickedRouteId || busy}
                            className="btn-primary text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40"
                            data-testid="change-route-start"
                        >
                            <Plus size={14} weight="bold" />
                            {busy ? "Starting…" : "Start route"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
